import { callFunction } from "./functions-client";

// Fleet-OS F6 — asset & trailer tracking. Register assets and check them out / in / locate
// from the phone (scan or type the tag), capturing custody + GPS. Shared by app + mob-app.
export const ASSET_TYPES = ["trailer", "container", "chassis", "reefer", "genset", "equipment", "other"] as const;
export type AssetType = (typeof ASSET_TYPES)[number];

export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  trailer: "Trailer", container: "Container", chassis: "Chassis", reefer: "Reefer unit",
  genset: "Genset", equipment: "Equipment", other: "Other",
};

export const ASSET_STATUSES = ["available", "in_use", "maintenance", "lost"] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];
export const ASSET_STATUS_LABELS: Record<AssetStatus, string> = {
  available: "Available", in_use: "In use", maintenance: "Maintenance", lost: "Lost",
};

export type CheckinAction = "check_out" | "check_in" | "locate";

export interface Asset {
  id: string; tag: string; type: AssetType; status: AssetStatus; label: string | null;
  held_by: string | null; last_lat: number | null; last_lng: number | null;
  last_seen_at: string | null; updated_at?: string; created_at?: string;
}
export interface Checkin {
  id: string; action: CheckinAction; by_name: string | null;
  lat: number | null; lng: number | null; note: string | null; created_at: string;
}
export interface Utilization { total: number; in_use: number; available: number; utilization_rate: number }

export const createAsset = (input: { tag: string; type?: AssetType; label?: string; notes?: string }) =>
  callFunction<{ asset: Asset }>("assets/create", { body: input });

export const listAssets = (status?: AssetStatus) =>
  callFunction<{ assets: Asset[]; utilization: Utilization }>(`assets/list${status ? `?status=${status}` : ""}`, { method: "GET" });

export const getAsset = (by: { id?: string; tag?: string }) => {
  const p = new URLSearchParams();
  if (by.id) p.set("id", by.id);
  if (by.tag) p.set("tag", by.tag);
  return callFunction<{ asset: Asset; checkins: Checkin[] }>(`assets/get?${p.toString()}`, { method: "GET" });
};

export const updateAsset = (input: { id: string; label?: string; notes?: string }) =>
  callFunction<{ ok: boolean }>("assets/update", { body: input });

export const setAssetStatus = (id: string, status: AssetStatus) =>
  callFunction<{ ok: boolean }>("assets/set-status", { body: { id, status } });

export const checkinAsset = (input: {
  id?: string; tag?: string; action: CheckinAction; by_name?: string; lat?: number; lng?: number; note?: string;
}) => callFunction<{ asset: Asset }>("assets/checkin", { body: input });

export const listCheckins = (id: string) =>
  callFunction<{ checkins: Checkin[] }>(`assets/checkins?id=${encodeURIComponent(id)}`, { method: "GET" });
